<template>
  <span class="audio-player" :class="{'audio-player--compact': compact}">
    <!-- Nativo quando há espaço: seek, volume, velocidade, teclado e rótulo
         acessível vêm de graça, e reimplementar isso com v-slider sairia pior.
         Na célula ele não cabe — o controle do Chrome tem ~300px de largura
         mínima —, então lá fica só o botão. -->
    <audio v-if="!compact" ref="audio" :src="src" :aria-label="label" controls preload="metadata"
           class="audio-player-native" @play="onNativePlay" @ended="playing = false"
           @pause="playing = false"/>

    <template v-else>
      <v-btn variant="text" icon size="x-small" class="no-drag" :title="actionLabel" :aria-label="actionLabel"
             @click.stop.prevent="toggle">
        <v-icon size="small">{{ playing ? 'mdi-pause' : 'mdi-play' }}</v-icon>
      </v-btn>
      <!-- preload=none: numa listagem de 100 linhas nada é baixado até o clique.
           O preço é não saber a duração antes de tocar pela primeira vez. -->
      <audio ref="audio" :src="src" :aria-label="label" preload="none"
             @ended="playing = false" @pause="playing = false" @play="playing = true"/>
    </template>
  </span>
</template>
<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import {pause, play, release} from '@/services/audioPlayback'

/**
 * Toca o áudio de uma propriedade. Duas formas: o controle nativo onde há espaço,
 * e um botão só na célula de tabela e no card.
 *
 * O estado de tocando mora aqui, e não no host, porque o próprio <audio> É o
 * estado — não há como içá-lo sem que cada célula perca o seu. É seguro desde que
 * as linhas da tabela tenham chave estável, o que o item-key="_instance" garante.
 */
@Component({name: 'ontos-audio-player'})
class AudioPlayer extends Vue {
  @Prop({type: String, required: true}) readonly src!: string
  @Prop({type: String, default: ''}) readonly label!: string
  @Prop({type: Boolean, default: false}) readonly compact!: boolean

  playing = false

  get actionLabel(): string {
    //O nome da propriedade junto: "Reproduzir" repetido cem vezes numa tabela não
    //diz nada a quem usa leitor de tela.
    const action = this.$tt(this.playing ? 'audio.pause' : 'audio.play') as string
    return this.label ? `${action} — ${this.label}` : action
  }

  get element(): HTMLAudioElement {
    return this.$refs.audio as HTMLAudioElement
  }

  toggle() {
    if (this.playing) pause(this.element)
    else play(this.element)
  }

  //O nativo tem play próprio; o singleton precisa saber para pausar os outros
  onNativePlay() {
    this.playing = true
    play(this.element)
  }

  beforeUnmount() {
    const element = this.$refs.audio as HTMLAudioElement | undefined
    if (element) release(element)
  }
}

export default toNative(AudioPlayer)
</script>
<style scoped>
.audio-player {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
}

.audio-player-native {
  max-width: 100%;
  height: 40px;
}
</style>
